import React, { useCallback, useEffect, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { isValid } from '@totalsoft/pure-validations-react';
import { initialState, reducer } from 'features/checklist/reducers/requiredDocumentProperties/containerReducer';
import { useValidation } from '@totalsoft/pure-validations-react';
import { checklistPropertiesValidator } from 'features/checklist/validators';
import ChecklistPropertyListComponent from './ChecklistPropertyListComponent';
import { useToast } from 'hooks/toasts/useToast';
import { useApolloClient } from '@apollo/client';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import useLanguageTracker from 'hooks/useLanguageTracker';
import { CHECKLIST_PROPERTIES_QUERY } from '../../queries';
import { ADD_UPDATE_CHECKLIST_TYPE_PROPERTIES } from '../../mutations';
import { CHECKLIST_TYPE_PROPERTIES_UPDATED } from '../../subscriptions';

const ChecklistPropertyListContainer = ({ data, reqDocTypeId }) => {
  const addToast = useToast();
  const [t] = useTranslation();
  const client = useApolloClient();

  const [localState, dispatch] = useReducer(reducer, initialState);
  const [saveDisabled, setSaveDisabled] = useState(false);

  const [checklistPropertiesValidation, validateChecklistProperties] = useValidation(checklistPropertiesValidator);

  // #region Queries

  const { data: propertiesData, refetch: refetchProperties } = useQuery(CHECKLIST_PROPERTIES_QUERY);

  // #endregion

  // #region Mutations

  const [addOrUpdateChecklistTypeProperties] = useMutation(ADD_UPDATE_CHECKLIST_TYPE_PROPERTIES);

  // #endregion

  // #region Subscriptions

  useSubscription(CHECKLIST_TYPE_PROPERTIES_UPDATED, {
    onData: _ => {
      client.cache.evict({ id: 'ChecklistItem', fieldName: 'requiredDocumentProperties' });
      addToast(t('Checklist.ChecklistPropertiesUpdated'), 'success');
      dispatch({ type: 'OnReqDocTypePropertyUpdated' });
    }
  });

  // #endregion

  // #region Handlers

  const handleAdd = () => {
    dispatch({ type: 'OnReqDocTypePropertyAdded', reqDocTypeId });
  };

  const handlePropertyChanged = (propName, propValue, index) => {
    dispatch({ type: 'OnReqDocTypePropertyChanged', propName, propValue, index });
  };

  const handleDelete = index => () => {
    if (window.confirm(t('Checklist.DeleteProperty'))) {
      dispatch({ type: 'OnReqDocTypePropertyDelete', index });
    }
  };

  const handleSave = () => {
    const input = {
      requiredDocProperties: localState.values,
      requiredDocTypeId: reqDocTypeId
    };

    setSaveDisabled(true);
    return addOrUpdateChecklistTypeProperties({ variables: { input } });
  };

  // #endregion

  // #region Side effects

  const onLanguageChange = useCallback(() => {
    refetchProperties();
  }, [refetchProperties]);

  useLanguageTracker({
    onChange: onLanguageChange
  });

  useEffect(() => {
    if (data) dispatch({ type: 'OnInitState', data });
  }, [data]);

  useEffect(() => {
    validateChecklistProperties(localState.values);
  }, [localState, validateChecklistProperties]);

  useEffect(() => {
    setSaveDisabled(!(checklistPropertiesValidation |> isValid));
  }, [localState, checklistPropertiesValidation]);

  // #endregion

  return (
    <ChecklistPropertyListComponent
      properties={localState}
      checklistProperties={propertiesData ? propertiesData.checklistProperties : []}
      onSave={handleSave}
      onAdd={handleAdd}
      onPropertyChanged={handlePropertyChanged}
      onDelete={handleDelete}
      validation={checklistPropertiesValidation}
      saveDisabled={saveDisabled}
    />
  );
};

ChecklistPropertyListContainer.propTypes = {
  data: PropTypes.array.isRequired,
  reqDocTypeId: PropTypes.number
};

export default ChecklistPropertyListContainer;
